import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

export type PageId = 'home' | 'services' | 'about' | 'contact';

interface NavigationContextType {
  activePage: PageId;
  setActivePage: (page: PageId) => void;
  navigateToPage: (page: PageId) => void;
  navigateToService: (serviceId: string) => void;
}

const NavigationContext = createContext<NavigationContextType | undefined>(undefined);

export const useNavigation = () => {
  const context = useContext(NavigationContext);
  if (!context) {
    throw new Error('useNavigation must be used within NavigationProvider');
  }
  return context;
};

interface NavigationProviderProps {
  children: ReactNode;
}

export const NavigationProvider: React.FC<NavigationProviderProps> = ({ children }) => {
  const [activePage, setActivePage] = useState<PageId>('home');
  const location = useLocation();
  const navigate = useNavigate();

  // Update active page based on current route
  useEffect(() => {
    const path = location.pathname;
    
    if (path === '/') {
      setActivePage('home');
    } else if (path === '/about') {
      setActivePage('about');
    } else if (path === '/contact') {
      setActivePage('contact');
    } else if (path.startsWith('/service/')) {
      setActivePage('services');
    } else {
      // Check if we're on a section anchor
      const hash = location.hash;
      if (hash === '#services') {
        setActivePage('services');
      } else if (hash === '#contact') {
        setActivePage('contact');
      }
    }
  }, [location]);

  const navigateToPage = (page: PageId) => {
    setActivePage(page);

    switch (page) {
      case 'home':
        navigate('/');
        break;
      case 'about':
        navigate('/about');
        break;
      case 'contact':
        navigate('/contact');
        break;
      case 'services':
        // Navigate to home first if not already there
        if (location.pathname !== '/') {
          navigate('/');
          // Wait for navigation to complete, then scroll to section
          setTimeout(() => {
            const element = document.getElementById(page);
            if (element) {
              element.scrollIntoView({ behavior: 'smooth' });
            }
          }, 100);
        } else {
          // Already on home, just scroll to section
          const element = document.getElementById(page);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
          }
        }
        break;
    }
  };

  const navigateToService = (serviceId: string) => {
    setActivePage('services');
    navigate(`/service/${serviceId}`);
  };

  return (
    <NavigationContext.Provider
      value={{
        activePage,
        setActivePage,
        navigateToPage,
        navigateToService,
      }}
    >
      {children}
    </NavigationContext.Provider>
  );
};
